import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { DuplicateAction, UploadFile } from '@hooks/useDocumentUpload';

interface DuplicateFileDialogProps {
  file: UploadFile | null;
  onResolve: (action: DuplicateAction) => void;
}

const megabytes = (bytes?: number) => (bytes ? `${(bytes / 1024 / 1024).toFixed(2)} MB` : null);

const DuplicateFileDialog: React.FC<DuplicateFileDialogProps> = ({ file, onResolve }) => {
  const { t, i18n } = useTranslation();

  const existing = file?.metadata;
  const existingVersion = existing?.existingVersion || 1;

  return (
    // Dismissing is a no-op: 'cancel' drops the queued file, and an Escape or a
    // stray click on the overlay is not a decision to discard an upload.
    <Dialog open={Boolean(file)} onOpenChange={() => {}}>
      {/* The only direct-child button is DialogContent's built-in close, which
          would now be inert; the three footer buttons are the real choice. */}
      <DialogContent className="revy-ui sm:max-w-lg [&>button]:hidden">
        <DialogHeader>
          <DialogTitle>{t('upload.duplicateDetected')}</DialogTitle>
          {file && (
            <DialogDescription>
              {t('upload.duplicateFileName', { name: file.file.name })}
            </DialogDescription>
          )}
        </DialogHeader>

        <Alert>
          <AlertDescription>{t('upload.duplicatePrompt')}</AlertDescription>
        </Alert>

        {file && existing && (
          <dl className="space-y-3 text-sm">
            <div>
              <dt className="font-medium">{t('upload.uploadingFile')}</dt>
              {/* The dialog description already names the file; repeating it here
                  said the same thing twice. */}
              <dd className="mt-1 text-muted-foreground revy-figure">
                {t('upload.fileSize')}: {megabytes(file.file.size)}
              </dd>
            </div>

            <div>
              <dt className="font-medium">{t('upload.existingFileInfo')}</dt>
              <dd className="mt-1 space-y-0.5 text-muted-foreground">
                <p>
                  {t('upload.title')}: {existing.existingTitle || file.file.name}
                </p>
                <p className="revy-figure">
                  {t('upload.currentVersion')}: {existingVersion}
                </p>
                <p className="revy-figure">
                  {t('upload.fileSize')}: {megabytes(existing.existingFileSize) ?? t('common.unknown')}
                </p>
                <p className="revy-figure">
                  {t('upload.uploadedAt')}:{' '}
                  {existing.existingUploadedAt
                    ? new Date(existing.existingUploadedAt).toLocaleString(i18n.language)
                    : t('common.unknown')}
                </p>
              </dd>
            </div>

            <Alert>
              <AlertDescription>
                {t('upload.newVersionInfo', { version: existingVersion + 1 })}
              </AlertDescription>
            </Alert>
          </dl>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="ghost" onClick={() => onResolve('cancel')}>
            {t('upload.cancelUpload')}
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onResolve('keep')}>
              {t('upload.saveAsSeparate')}
            </Button>
            <Button onClick={() => onResolve('replace')}>{t('upload.saveAsNewVersion')}</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateFileDialog;
